/**
 * RecipientEditor.jsx — the typed recipient-spec list editor for notification
 * flows: rows of { kind, value?, channel? } with a channel (To/Cc/Bcc) select,
 * a kind select, and a kind-appropriate value control. Extracted from
 * NotificationsAdmin.jsx so the notification-rule wizard page can reuse it
 * without pulling in the whole admin surface.
 *
 * Kinds mirror RECIPIENT_KINDS (@shared/notificationSchema.js). The 'group'
 * kind picks from the admin-managed `recipientGroups` config — pass the loaded
 * groups via the `groups` prop ([{ key, label, emails[] }]); an unknown key
 * soft-fails to zero addresses at send time, so the picker keeps a missing key
 * selectable rather than silently rewriting the spec.
 *
 * The 'Role / job title' kind is one searchable list over three spec kinds:
 * the fixed ROLE_PRESETS ('role'), every job title currently held in Entra
 * ('jobTitle') and every AE Tracker app role ('appRole'). The list comes from
 * GET /api/notifications/recipient-options, fetched once per page load.
 *
 * The channel select adds EXCEPT after To/Cc/Bcc: that row's addresses are
 * removed from everyone the other rows resolve to (e.g. a role except its head).
 *
 * Server-side floors (not re-checked here): staff-domain addresses only,
 * max 50 resolved recipients per dispatch.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { RECIPIENT_KINDS, ROLE_PRESETS, RECIPIENT_CHANNELS, EXCEPT_CHANNEL } from '@shared/notificationSchema.js';
import SearchableSelect from './SearchableSelect.jsx';
import { getNotificationRecipientOptions } from '../api/client.js';

export const KIND_LABELS = {
  email:                    'Email address',
  horo_by_region:          'Head of RO (by case region)',
  clinic_manager_by_clinic:'Clinic managers + assistant manager (by case clinic)',
  country_manager_by_ops:  'Country manager (by case country)',
  jobTitle:                'By job title',
  role:                    'Role / job title',
  group:                   'Recipient group',
  appRole:                 'AE role',
};
export const KIND_NEEDS_VALUE = new Set(['email', 'jobTitle', 'role', 'group', 'appRole']);

// The three kinds the searchable Role list writes; the kind select shows them
// all as 'Role / job title'.
const ROLE_LIST_KINDS = new Set(['role', 'jobTitle', 'appRole']);
const KIND_SELECT_OPTIONS = RECIPIENT_KINDS.filter(k => k !== 'jobTitle' && k !== 'appRole');

// One fetch per page load, shared by every picker row. _appRoleLabels lets
// recipientSummary show a role's name instead of its Entra value once loaded.
let _optionsPromise = null;
let _appRoleLabels = {};
function loadRecipientOptions() {
  if (!_optionsPromise) {
    _optionsPromise = getNotificationRecipientOptions().then(d => {
      _appRoleLabels = Object.fromEntries((d?.appRoles || []).map(r => [r.value, r.label]));
      return d;
    }).catch(e => {
      _optionsPromise = null;   // let the next mount retry
      return { jobTitles: [], appRoles: [], error: e?.message || 'Could not load the role list.' };
    });
  }
  return _optionsPromise;
}

function RoleListPicker({ spec, onChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    loadRecipientOptions().then(d => { if (live) setData(d); });
    return () => { live = false; };
  }, []);

  const options = useMemo(() => {
    const opts = [
      ...Object.entries(ROLE_PRESETS).map(([k, v]) => ({
        value: `role:${k}`, label: v.label, sub: v.jobTitle, group: 'Presets',
      })),
      // Counts sit in `sub`, not `meta`: SearchableSelect repeats meta on the
      // closed trigger, which wrapped and made picker rows uneven heights.
      ...(data?.appRoles || []).map(r => ({
        value: `appRole:${r.value}`, label: r.label, sub: `${r.assigned} assigned`, group: 'AE roles',
      })),
      ...(data?.jobTitles || []).map(t => ({
        value: `jobTitle:${t.title}`, label: t.title, sub: `${t.count} ${t.count === 1 ? 'person' : 'people'}`, group: 'Job titles',
      })),
    ];
    // Keep a stored value selectable even when nobody currently holds it.
    const current = spec.value ? `${spec.kind}:${spec.value}` : '';
    if (current && data && !opts.some(o => o.value === current)) {
      opts.unshift({ value: current, label: spec.value, sub: 'Not currently held by anyone', group: 'Saved' });
    }
    return opts;
  }, [data, spec.kind, spec.value]);

  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <SearchableSelect
        value={spec.value ? `${spec.kind}:${spec.value}` : ''}
        options={options}
        placeholder={data ? 'Search roles and job titles…' : 'Loading roles…'}
        onChange={e => {
          const raw = String(e.target.value || '');
          const at = raw.indexOf(':');
          onChange({ ...spec, kind: raw.slice(0, at), value: raw.slice(at + 1) });
        }}
      />
      {data?.error && <div className="field-hint" style={{ color: 'var(--danger)' }}>{data.error}</div>}
    </div>
  );
}

/** One-line human summary of a spec list (rule cards, config summaries). */
export function recipientSummary(specs, groups = []) {
  if (!Array.isArray(specs) || !specs.length) return 'No recipients';
  return specs.map(s => {
    let label;
    if (s.kind === 'email') label = s.value;
    else if (s.kind === 'role') label = ROLE_PRESETS[s.value]?.label || s.value;
    else if (s.kind === 'jobTitle') label = `“${s.value}”`;
    else if (s.kind === 'appRole') label = `${_appRoleLabels[s.value] || s.value} (AE role)`;
    else if (s.kind === 'group') {
      const g = (groups || []).find(g => g.key === s.value);
      label = g?.label ? `${g.label} (group)` : `Group “${s.value}”`;
    }
    else label = KIND_LABELS[s.kind] || s.kind;
    if (s.channel === EXCEPT_CHANNEL) return `except ${label}`;
    return s.channel && s.channel !== 'to' ? `${label} (${s.channel})` : label;
  }).join(' · ');
}

// ── Recipient picker — a list of typed { kind, value } specs ──────────────────
export default function RecipientEditor({ specs, onChange, groups = [] }) {
  const list = Array.isArray(specs) ? specs : [];
  const setAt = (i, next) => onChange(list.map((s, j) => (j === i ? next : s)));
  const add = () => onChange([...list, { kind: 'jobTitle', value: '' }]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));

  // A stored spec may reference a group that was since deleted — keep it in the
  // select so the row shows what is saved (it resolves to nobody until fixed).
  const groupOptions = (spec) =>
    spec.value && !(groups || []).some(g => g.key === spec.value)
      ? [...groups, { key: spec.value, label: `${spec.value} (missing)` }]
      : groups;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.map((spec, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="input"
            value={spec.channel || 'to'}
            onChange={e => setAt(i, { ...spec, channel: e.target.value })}
            title="Delivery channel — EXCEPT removes these people from the other rows"
            style={{ fontSize: 13, padding: '6px 8px', flex: '0 0 88px' }}
          >
            {[...RECIPIENT_CHANNELS, EXCEPT_CHANNEL].map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
          <select
            className="input"
            value={ROLE_LIST_KINDS.has(spec.kind) ? 'role' : spec.kind}
            onChange={e => {
              // 'Role / job title' starts empty: the row's error asks for a pick.
              const kind = e.target.value === 'role' ? 'jobTitle' : e.target.value;
              const value = kind === 'group' ? (groups[0]?.key || '') : '';
              setAt(i, { ...spec, kind, value: KIND_NEEDS_VALUE.has(kind) ? value : undefined });
            }}
            style={{ fontSize: 13, padding: '6px 10px', flex: '0 0 220px' }}
          >
            {KIND_SELECT_OPTIONS.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>

          {ROLE_LIST_KINDS.has(spec.kind) && (
            <RoleListPicker spec={spec} onChange={next => setAt(i, next)} />
          )}
          {spec.kind === 'email' && (
            <input type="email" className="input" placeholder="name@therapieclinic.com" value={spec.value || ''}
              onChange={e => setAt(i, { ...spec, value: e.target.value })}
              style={{ fontSize: 13, padding: '6px 10px', flex: 1, minWidth: 180 }} />
          )}
          {spec.kind === 'group' && (
            groups.length || spec.value ? (
              <select className="input" value={spec.value || ''} onChange={e => setAt(i, { ...spec, value: e.target.value })}
                style={{ fontSize: 13, padding: '6px 10px', flex: 1, minWidth: 180 }}>
                {!spec.value && <option value="">Choose a group…</option>}
                {groupOptions(spec).map(g => <option key={g.key} value={g.key}>{g.label || g.key}</option>)}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>
                No recipient groups yet — create one under “Recipient groups”.
              </span>
            )
          )}
          {!KIND_NEEDS_VALUE.has(spec.kind) && (
            <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>resolved from the case</span>
          )}

          <button type="button" className="btn ghost sm" onClick={() => remove(i)} aria-label="Remove recipient" style={{ fontSize: 12 }}>✕</button>
        </div>
      ))}
      <div>
        <button type="button" className="btn ghost sm" onClick={add} style={{ fontSize: 12 }}>+ Add recipient</button>
      </div>
      {!list.length && <div className="field-hint">Add at least one recipient. Notifications only reach staff (therapieclinic.com).</div>}
    </div>
  );
}
